import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import api from "services/api";
import SignedDownloadButton from "pages/Clients/sections/desc/SignedDownloadButton";

import { useTranslation } from "react-i18next";

import bgImage from "assets/images/bg-user-info.jpg";

function AndroidHero({ platform, clientKey }) {
  const { t } = useTranslation();

  const [software, setSoftware] = useState(null);
  const softwareItems = software?.items || [];
  const arm64Software = softwareItems.find((item) => item.arch === "arm64-v8a");
  const armeabiSoftware = softwareItems.find((item) => item.arch === "armeabi-v7a");

  useEffect(() => {
    api
      .get(`/software/${platform}/`)
      .then((res) => {
        setSoftware(res.data);
      })
      .catch(() => {});
  }, [platform]);

  const renderDownloadGroup = (item, archKey) => {
    if (!item) return null;
    return (
      <MKBox
        key={archKey}
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "center", md: "flex-start" }}
        gap={0.8}
        sx={{
          width: "100%",
          maxWidth: 360,
          pb: 1.5,
          "&:not(:last-of-type)": {
            borderBottom: "1px solid rgba(255,255,255,0.18)",
          },
        }}
      >
        <MKTypography variant="body2" color="white" fontWeight="bold">
          {t(`clients.${clientKey}.downloads.${archKey}.title`)}
        </MKTypography>
        <MKTypography variant="caption" color="white" opacity={0.72} lineHeight={1.4}>
          {t(`clients.${clientKey}.downloads.${archKey}.hint`)}
        </MKTypography>
        <MKBox
          display="flex"
          flexWrap="wrap"
          justifyContent={{ xs: "center", md: "flex-start" }}
          gap={1}
        >
          {item.signed_link_url ? (
            <SignedDownloadButton
              variant="outlined"
              color="white"
              linkUrl={item.signed_link_url}
              label={t(`clients.${clientKey}.downloadApk`)}
            />
          ) : null}
          {item.backup_signed_link_url ? (
            <SignedDownloadButton
              variant="outlined"
              color="white"
              linkUrl={item.backup_signed_link_url}
              label={t(`clients.${clientKey}.downloadZip`)}
            />
          ) : null}
        </MKBox>
      </MKBox>
    );
  };

  return (
    <MKBox
      minHeight="52vh"
      width="100%"
      sx={({ breakpoints }) => ({
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "grid",
        placeItems: "center",
        paddingTop: "5rem",
        paddingBottom: "5rem",
        [breakpoints.down("md")]: {
          paddingTop: "6rem",
          paddingBottom: "6rem",
        },
      })}
    >
      <Container>
        <Grid container spacing={3} alignItems="center">
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              textAlign: "center",
              mt: { xs: 2, md: 0 },
            }}
          >
            <MKTypography variant="h4" color="white" fontWeight="bold">
              {software?.name || t(`clients.${clientKey}.softwareName`)}
            </MKTypography>

            <MKTypography variant="body2" color="white" opacity={0.8} mt={1}>
              {t(`clients.${clientKey}.supportedDevices`)}
            </MKTypography>

            <MKTypography variant="body2" color="white" opacity={0.8} mt={0.5}>
              {t(`clients.${clientKey}.currentVersionLabel`)}:{" "}
              {software?.version || t(`clients.${clientKey}.version`)}
            </MKTypography>
            <MKBox
              mt={2.5}
              display="flex"
              flexDirection="column"
              alignItems={{ xs: "center", md: "flex-start" }}
              gap={1.5}
            >
              {renderDownloadGroup(arm64Software, "arm64")}
              {renderDownloadGroup(armeabiSoftware, "armeabi")}
            </MKBox>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              mt: { xs: 4, md: 0 },
            }}
          >
            <MKTypography variant="h5" color="white" fontWeight="bold">
              {t(`clients.${clientKey}.archGuide.title`)}
            </MKTypography>

            <MKTypography variant="body2" color="white" opacity={0.85} mt={1}>
              {t(`clients.${clientKey}.archGuide.arm64`)}
            </MKTypography>
            <MKTypography variant="body2" color="white" opacity={0.85} mt={0.5}>
              {t(`clients.${clientKey}.archGuide.armeabi`)}
            </MKTypography>
            <MKTypography variant="body2" color="white" opacity={0.85} mt={0.5}>
              {t(`clients.${clientKey}.archGuide.apk`)}
            </MKTypography>
            <MKTypography variant="body2" color="white" opacity={0.85} mt={0.5}>
              {t(`clients.${clientKey}.archGuide.zip`)}
            </MKTypography>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

AndroidHero.defaultProps = {
  platform: "android",
  clientKey: "android",
};

AndroidHero.propTypes = {
  platform: PropTypes.string,
  clientKey: PropTypes.string,
};

export default AndroidHero;
